import React, { useEffect, useRef, useState } from "react";

import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import FormHelperText from "@mui/material/FormHelperText";

import {
  LocationSuggestion,
  ZipCodeSuggestion,
} from "../../../types/fieldTypes";
import { t } from "i18next";

const styles = {
  autocomplete: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "6px",
      background: "#f8f9fd !important;",
    },
  },
};

export default function AutoCompleteComponent(props: any) {
  const {
    options,
    selectedValue,
    getOptionLabel,
    onChange,
    inputValue,
    onInputChange,
    errorMsg,
    isApplicationSubmitted,
    label,
    field,
    isExpandedView,
    sectionName,
    propertyId,
    unitId,
    loanDetails,
    dynamicConfig,
    currentObjFieldError,
  } = props;

  const [localInputValue, setLocalInputValue] = useState(inputValue ?? "");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isSelectingRef = useRef(false); // track if user just selected

  // sync only when parent resets externally (form clear etc.)
  useEffect(() => {
    if (!isSelectingRef.current) {
      setLocalInputValue(inputValue ?? "");
    }
    isSelectingRef.current = false; // reset after every sync
  }, [inputValue]);

  // sync if parent resets the value externally
  useEffect(() => {
    setLocalInputValue(inputValue ?? "");
  }, [inputValue]);

  useEffect(() => {
    if (options?.length > 0 && dropdownOpen) {
      setDropdownOpen(false);
      setTimeout(() => setDropdownOpen(true), 0); // reopen on next tick
    }
  }, [options]);

  const checkIsDisabled = (field: any) => {
    const { isDisabled, isAutoCalculated } = field;
    if (isDisabled) {
      return true;
    }
    if (
      dynamicConfig?.allowEditAfterSubmit &&
      !(
        isAutoCalculated &&
        isAutoCalculated({
          sizingType: loanDetails?.sizingType,
        })
      )
    ) {
      return false;
    }
    if (
      isApplicationSubmitted ||
      (isAutoCalculated &&
        isAutoCalculated({
          sizingType: loanDetails?.sizingType,
        }))
    ) {
      return true;
    }
  };

  return (
    <FormControl variant="outlined" sx={{ width: "100%" }}>
      <Autocomplete
        key={`${field?.id}-${field.label}`} // reset when options change
        open={dropdownOpen}
        onOpen={() => setDropdownOpen(true)}
        onClose={() => setDropdownOpen(false)}
        className={`truncate-words margin-0 ${
          currentObjFieldError && !!errorMsg
            ? "app-theme-data-points-error"
            : ""
        } `}
        style={{ margin: 0 }}
        options={options ?? []}
        size="small"
        filterOptions={(x: any) => x}
        filterSelectedOptions
        disableClearable
        value={selectedValue ?? null}
        getOptionLabel={getOptionLabel}
        disabled={checkIsDisabled(field)}
        onChange={(
          event: React.SyntheticEvent<Element, Event>,
          newValue: LocationSuggestion | ZipCodeSuggestion | null,
        ) => {
          onChange(
            newValue,
            "autoComplete",
            field,
            sectionName,
            propertyId,
            unitId,
          );
        }}
        inputValue={localInputValue}
        onInputChange={(event: React.SyntheticEvent<Element, Event>, value: string, reason: string,
) => {
          if (reason === "reset" || reason === "clear") {
            isSelectingRef.current = true;
            setLocalInputValue(value);
            return; // don't call parent onInputChange on selection
          }
          setLocalInputValue(value);  // update local state instantly
          onInputChange(              // notify parent
            value,
            ["address", "postalCode"]?.includes(field?.id)
              ? "autoComplete"
              : field.id,
            field,
            sectionName,
            propertyId,
            unitId,
          );
        }}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            value={localInputValue || ""}
            margin="dense"
            className={`${currentObjFieldError && !!errorMsg && "app-theme-data-points-error"}`}
            style={
              isExpandedView
                ? { width: "calc(100% - 2px)", margin: "0px" }
                : {
                    width: "100%",
                    margin: "0px",
                  }
            }
            variant="outlined"
            disabled={checkIsDisabled(field)}
            sx={
              (styles.autocomplete,
              { borderRadius: "6px", background: "#f8f9fd !important;" })
            }
          />
        )}
        selectOnFocus
      />
      {currentObjFieldError ? (
        <FormHelperText className="break-word">{t(errorMsg)}</FormHelperText>
      ) : null}
    </FormControl>
  );
}
